/**
 * 爱微圈导航 · 通用模态 / 数据备份
 * 本文件是运行时源（唯一真源）—— 由 tools/split_appjs.py 从 assets/js/app.js 一次性
 * 拆分而来，此后各模块独立维护。**改前端逻辑就改这里**，改完执行 `bash build.sh`
 * 打包成 assets/js/app.min.js（index.php 自动优先引用 .min 产物）。
 * assets/js/app.js 只是拆分时的原始素材，运行时不再加载，勿改。
 */

import { applyWeather, greeting, loadDailyContent, loadWeather, renderClockGreet, renderWeatherPop, tickClock, toggleWeatherPop, weatherGeolocate } from './clock.js';
import { $, $$, API, API_TIMEOUT_MS, CACHE_KEY_LEGACY, DEFAULT_SETTINGS, apiGet, apiPost, bootData, cacheKey, debounce, esc, escAttr, fetchWithTimeout, randomColor, safeUrl, shouldAnimate, state, toast } from './core.js';
import { CAT_ICONS, ICO_CHECK, ICO_COPY, ICO_EDIT, ICO_EXPAND, ICO_PIN, ICO_TRASH, NOTE_COLORS, addNote, addTodo, copyNote, loadCategories, loadNotes, loadTodos, loadWebsiteIcon, loadWebsites, observeWebsiteIcons, openNoteEditor, preloadAdjacentCategories, relTime, renderCategories, renderNotes, renderTodos, renderWebsiteList, startTodoEdit, toggleTodo, websiteIconUrls } from './data.js';
import { showContextMenu, showEditModal, showFolderModal } from './edit.js';
import { BUILTIN_ACTIONS, init } from './main.js';
import { ONBOARDING_STEPS, closeOnboarding, maybeShowOnboarding, nextOnbStep, renderOnbStep, showOnboarding } from './onboarding.js';
import { batchDelete, batchMoveToPage, enforcePageCapacity, getPageCapacity, goNextPage, goPrevPage, initBatchToolbar, loadShortcuts, loadShortcutsRaw, prefetchAdjacentPages, prefetchPage, renderPages, shiftPagesAfterRemoval, switchPage, toggleOrganizeMode, updateBatchToolbar, updatePageArrows } from './pages.js';
import { _readIconStore, _reduceMotion, _saveIconStore, _scFp, cardRenderFp, currentGrid, faviconOf, flipMove, getGridCols, gridRenderFp, persistGridReorder, preconnectUrl, renderCardView, renderCardViewAll, renderIcon, renderShortcuts, renderSkeleton, waitTransition } from './render.js';
import { TAB_TARGETS, bindEngineIconFallback, currentEngine, engineBadgeHtml, engineFamily, fetchSuggestions, handleSearch, hideSuggestions, openTarget, renderEngineBadge, renderEngineDropdown, showEngineAddModal } from './search.js';
import { applySettings, bindSettingsUI, loadSettings, scheduleSave } from './settings.js';
import { bindDonateCard, bindShareCard, detectPayApp, fmtDonateTime, loadDonateRank, renderDonateRank, shareQrSrc, shareTargetUrl, shareTextDesc, shareTextTitle, showAboutModal, showContactModal, showDonateModal, showFeedbackModal, showPrivacyModal, showShareModal, showTermsModal } from './share.js';
import { closeDrawers, closeSidebar, doLogout, formatTime, loadUserCategories, openDrawer, openSidebar, renderProfile, resetDataView, showAccountModal, showAuthModal, showCategoryMgr, showCustomAddModal, showDeleteAccountModal, showEmailChangeModal, showForgotPasswordModal, showPhoneBindModal, showQuickAddModal, showSyncChoiceModal, switchSidebarTab } from './sidebar.js';
import { CACHE_TTL, CATEGORY_ENGINE_MAP, applySearchTabUI, cacheState, disableNotifications, enableNotifications, mapCategoryToEngine, notifyEnabled, restoreCache, setNotifyEnabled, showNotification, submitSiteForReview, syncEnabled, toggleSync, trackVisit } from './sync.js';
import { SWATCH_COLORS, showWallpaperModal } from './wallpaper.js';

export function openModal(html) {
    const overlay = $('#modalOverlay');
    const box = $('#modalBox');
    const inner = $('#modalInner');
    inner.innerHTML = html;
    overlay.style.display = 'flex';
    // 锁定 body 滚动，避免 modal 打开时背景滚动
    document.body.classList.add('modal-open');
    const x = $('#modalCloseX');
    if (x) x.onclick = closeModal;
    $$('[data-close-modal]', inner).forEach(b => b.onclick = closeModal);
    setTimeout(() => {
        const first = $('input', inner) || $('button', inner);
        if (first) first.focus();
    }, 60);
}

export function closeModal() {
    $('#modalOverlay').style.display = 'none';
    document.body.classList.remove('modal-open');
}

export function showModalError(sel, msg) {
    const el = $(sel);
    if (el) { el.textContent = msg; el.classList.add('show'); }
}

export function modalConfirm(title, text) {
    return new Promise(resolve => {
        openModal(`
            <div class="modal-title">${esc(title)}</div>
            <div style="font-size:14px;color:var(--text-dim);text-align:center;margin-bottom:8px">${esc(text)}</div>
            <div class="modal-actions">
                <button class="btn btn-light" id="cfNo">取消</button>
                <button class="btn btn-primary" id="cfYes">确认</button>
            </div>`);
        $('#cfNo').onclick = () => { closeModal(); resolve(false); };
        $('#cfYes').onclick = () => { closeModal(); resolve(true); };
    });
}

/* ---------- 数据导入导出 ---------- */

export function setupDataButtons() {
    $('#itemExport').onclick = () => { location.href = API.sync + '?action=export'; };
    $('#itemImport').onclick = () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json,application/json';
        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;
            if (file.size > 5 * 1024 * 1024) { toast('备份文件过大（上限 5MB）', 'error'); return; }
            try {
                const data = JSON.parse(await file.text());
                const res = await apiPost(API.sync + '?action=import', { data });
                toast(`导入完成：快捷 ${res.shortcuts} / 设置 ${res.settings} / 待办 ${res.todos} / 便签 ${res.notes}`, 'success');
                loadShortcuts(); loadTodos(); loadNotes(); loadSettings();
            } catch (e) { toast(e.message || '备份文件解析失败', 'error'); }
        };
        input.click();
    };
    $('#itemSnapshot').onclick = async () => {
        try {
            const res = await apiPost(API.sync + '?action=snapshot', {});
            toast(`已创建快照（共 ${res.count} 份）`, 'success');
        } catch (e) { toast(e.message, 'error'); }
    };
    $('#itemRepair').onclick = () => runDataRepair();

    // 云同步
    const syncToggle = $('#syncToggle');
    if (syncToggle) {
        syncToggle.onclick = async () => {
            if (!state.user || state.user.is_guest) { showAuthModal('login'); return; }
            try {
                const enabled = syncToggle.classList.contains('on') ? 0 : 1;
                const res = await apiPost(API.auth + '?action=toggle_sync', { enabled });
                state.user = res.user;
                syncToggle.classList.toggle('on', !!res.user.sync_enabled);
                toast(res.user.sync_enabled ? '云同步已开启' : '云同步已关闭', 'success');
            } catch (e) { toast(e.message, 'error'); }
        };
    }
    $('#itemCloudBackup').onclick = () => createCloudBackup();
    $('#itemBackupNodes').onclick = () => showBackupList();
    $('#itemQrSync').onclick = () => showQrSync();

    // 反馈与分享
    $('#itemFeedback').onclick = () => showFeedbackModal();
    bindShareCard();
    bindDonateCard();

    // 关于
    $('#itemVersion').onclick = () => showAboutModal();
    $('#itemAbout').onclick = () => showAboutModal();
    $('#itemContact').onclick = () => showContactModal();
    $('#itemPrivacy').onclick = () => showPrivacyModal();
    $('#itemTerms').onclick = () => showTermsModal();
}

/* ---------- 云备份 ---------- */

export async function createCloudBackup() {
    if (!state.user || state.user.is_guest) { showAuthModal('login'); return; }
    toast('正在备份...', 'info');
    try {
        const res = await apiPost(API.sync + '?action=cloud_backup', {});
        state.user.last_sync = res.created_at;
        if (state.user) state.user.sync_enabled = 1;
        renderProfile();
        toast(`备份成功（${res.size_text}）`, 'success');
    } catch (e) { toast('备份失败：' + e.message, 'error'); }
}


export async function showBackupList() {
    if (!state.user || state.user.is_guest) { showAuthModal('login'); return; }
    openModal(`
        <div class="modal-title">历史备份</div>
        <div class="modal-actions" style="margin-bottom:12px"><button class="btn btn-primary btn-sm" id="backupNew">+ 新建备份</button></div>
        <div class="backup-list" id="backupListInner"><div class="backup-empty">加载中...</div></div>
        <div class="modal-actions" style="margin-top:14px"><button class="btn btn-light" data-close-modal>关闭</button></div>`);
    $('#backupNew').onclick = async () => {
        await createCloudBackup();
        loadBackupListInto($('#backupListInner'));
    };
    loadBackupListInto($('#backupListInner'));
}


export async function loadBackupListInto(container) {
    try {
        const res = await apiGet(API.sync + '?action=backup_list');
        const backups = res.backups || [];
        if (!backups.length) {
            container.innerHTML = '<div class="backup-empty">' + (state.site.empty_backup || '暂无备份，点击上方按钮创建') + '</div>';
            return;
        }
        container.innerHTML = backups.map(b => `
            <div class="backup-item" data-id="${b.id}">
                <div class="backup-icon"><svg viewBox="0 0 24 24"><path d="M12 3a9 9 0 000 18c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1-.23-.27-.38-.62-.38-1 0-.83.67-1.5 1.5-1.5H16a5 5 0 005-5c0-4.42-4.03-8-9-8z"/></svg></div>
                <div class="backup-info">
                    <div class="backup-name">${esc(b.name || '备份')}</div>
                    <div class="backup-meta">${b.size_text} · ${formatTime(b.created_at)}</div>
                </div>
                <div class="backup-actions">
                    <button title="恢复" data-act="restore"><svg viewBox="0 0 24 24"><path d="M12 5V1L7 6l5 5V7a5 5 0 11-5 5H5a7 7 0 107-7z"/></svg></button>
                    <button title="删除" class="danger" data-act="delete"><svg viewBox="0 0 24 24"><path d="M6 19a2 2 0 002 2h8a2 2 0 002-2V7H6v12zM19 4h-3.5l-1-1h-5l-1 1H5v2h14V4z"/></svg></button>
                </div>
            </div>`).join('');
        container.querySelectorAll('.backup-item').forEach(item => {
            const id = item.dataset.id;
            item.querySelector('[data-act="restore"]').onclick = async () => {
                if (!await modalConfirm('恢复备份', '恢复将覆盖当前所有数据，确定继续？')) return;
                try {
                    await apiPost(API.sync + '?action=backup_restore', { id: Number(id) });
                    closeModal();
                    toast('备份已恢复', 'success');
                    loadShortcuts(); loadTodos(); loadNotes(); loadSettings();
                } catch (e) { toast('恢复失败：' + e.message, 'error'); }
            };
            item.querySelector('[data-act="delete"]').onclick = async () => {
                if (!await modalConfirm('删除备份', '确定删除此备份？此操作不可撤销。')) return;
                try {
                    await apiPost(API.sync + '?action=backup_delete', { id: Number(id) });
                    loadBackupListInto(container);
                    toast('备份已删除', 'success');
                } catch (e) { toast('删除失败：' + e.message, 'error'); }
            };
        });
    } catch (e) {
        container.innerHTML = '<div class="backup-empty">加载失败：' + esc(e.message) + '</div>';
    }
}

/* ---------- 二维码同步 ---------- */

export function showQrSync() {
    if (!state.user || state.user.is_guest) { showAuthModal('login'); return; }
    const syncUrl = location.origin + location.pathname + '?sync=1';
    openModal(`
        <div class="modal-title">扫码同步到手机</div>
        <div class="qr-container">
            <div class="qr-box" id="qrBox"><div style="display:flex;align-items:center;justify-content:center;height:100%;color:#999;font-size:13px">生成中...</div></div>
            <div class="qr-hint">使用手机浏览器扫描二维码</div>
            <div class="qr-url">${esc(syncUrl)}</div>
        </div>
        <div class="modal-actions"><button class="btn btn-light" data-close-modal>关闭</button></div>`);
    // 使用在线 QR 码生成服务（轻量方案）
    const qrUrl = 'https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=' + encodeURIComponent(syncUrl);
    const img = new Image();
    img.onload = () => {
        const box = $('#qrBox');
        if (box) { box.innerHTML = ''; box.appendChild(img); img.style.width = '100%'; img.style.height = '100%'; }
    };
    img.onerror = () => {
        const box = $('#qrBox');
        if (box) box.innerHTML = '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#999;font-size:12px;text-align:center;padding:10px">二维码加载失败<br>请手动输入网址</div>';
    };
    img.src = qrUrl;
}

/* ---------- 数据修复 ---------- */

export async function runDataRepair() {
    if (!await modalConfirm('数据修复', '将自动检测并修复异常数据（孤立项、重复位置、无效链接等），是否继续？')) return;
    try {
        const res = await apiPost(API.sync + '?action=repair', {});
        const fixes = [
            ['孤立快捷方式', res.orphans || 0],
            ['重复位置序号', res.positions || 0],
            ['无效链接修复', res.urls || 0],
            ['空名称修复', res.names || 0],
        ];
        openModal(`
            <div class="modal-title">修复完成</div>
            <div class="repair-result">
                ${fixes.map(([label, count]) => `
                    <div class="repair-item">
                        <span class="repair-item-label">${label}</span>
                        <span class="repair-item-count ${count === 0 ? 'zero' : ''}">${count > 0 ? '修复 ' + count + ' 项' : '无异常'}</span>
                    </div>`).join('')}
            </div>
            <div class="modal-actions" style="margin-top:16px"><button class="btn btn-primary" data-close-modal>完成</button></div>`);
        if (res.orphans || res.positions || res.urls || res.names) {
            loadShortcuts();
        }
    } catch (e) { toast('修复失败：' + e.message, 'error'); }
}

/* ---------- 社交分享 / 反馈 / 捐赠 ---------- */
/* ---------- 分享/打赏（内容后台可配置） ---------- */

